import Header from '@/components/header/Header.vue';
import Footer from '@/components/footer/Footer.vue';
import axios from '@/config/axios';

export default {
  name: 'OldBookForm',
  components: {
    Header,
    Footer
  },
  data() {
    return {
      activeStep: 1, // 1, 2, 3
      isEditMode: false,
      editBoothId: null,
      editBoothData: null,
      isLoadingEdit: false,
      isSubmitting: false,

      // Booth Configuration Form
      boothForm: {
        name: '',
        saleType: 0, // 0: ALLOW_SINGLE, 1: COMBO_ONLY
        totalBooks: 1, // 1 to 10
        weight: 350,
        price: '', // Combo price
        description: ''
      },

      stackFile: null,
      stackPreview: null,

      // Books in Booth
      currentBookIndex: 0,
      books: [
        this.createEmptyBook()
      ],
      bookFiles: [
        { cover: null, coverBack: null }
      ],
      bookPreviews: [
        { cover: null, coverBack: null }
      ],

      // Catalog Lookup State
      catalogSearchKey: '',
      catalogDebounceTimer: null,
      catalogSuggestions: [],
      showCatalogDropdown: false,
      isSearchingCatalog: false,

      // Preview & Completion States
      activePreviewMedia: null,
      activePreviewKey: null,
      activePreviewLabel: null,
      createdBoothId: null,

      // Zoom Modal State
      showZoomModal: false,
      zoomImage: null,
      zoomScale: 1,
      zoomPanX: 0,
      zoomPanY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      toolbarHidden: false,

      // Category Selection State
      categoryOptions: [],
      isCategoriesLoading: false,
      showCategoryDropdown: false,
      categorySearchQuery: '',

      // Condition Options Reference
      conditionOptions: [
        { value: 4, label: 'Cũ', range: '< 50%', desc: 'Sách lâu năm, bìa hoặc gáy có nếp gấp hay vết gập.' },
        { value: 3, label: 'Trung bình', range: '50 - 69%', desc: 'Có gạch chân hoặc trang ngả vàng nhẹ theo thời gian.' },
        { value: 2, label: 'Khá mới', range: '70 - 84%', desc: 'Có dấu vết thời gian nhẹ, gáy sách và chữ đọc rất rõ.' },
        { value: 1, label: 'Như mới', range: '85 - 94%', desc: 'Sách còn sạch đẹp, trang sách phẳng, không gạch xóa.' },
        { value: 0, label: 'Rất mới', range: '95 - 100%', desc: 'Mới mua, chưa qua sử dụng hoặc chỉ lướt sơ.' }
      ]
    };
  },
  computed: {
    currentBook() {
      return this.books[this.currentBookIndex] || this.books[0];
    },
    currentBookPreview() {
      return this.bookPreviews[this.currentBookIndex] || { cover: null, coverBack: null };
    },
    formattedComboPrice: {
      get() {
        if (!this.boothForm.price && this.boothForm.price !== 0) return '';
        const num = parseInt(this.boothForm.price, 10);
        if (isNaN(num)) return '';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      },
      set(newVal) {
        if (!newVal) {
          this.boothForm.price = '';
          return;
        }
        const raw = String(newVal).replace(/\D/g, '');
        this.boothForm.price = raw ? parseInt(raw, 10) : '';
      }
    },
    calculatedTotalPrice() {
      return this.books.reduce((sum, b) => sum + (parseInt(b.price, 10) || 0), 0);
    },
    effectivePrice() {
      if (this.boothForm.saleType === 1) {
        return parseInt(this.boothForm.price, 10) || 0;
      }
      return this.calculatedTotalPrice;
    },
    calculatedTotalOriginalPrice() {
      return this.books.reduce((sum, b) => sum + (parseInt(b.originalPrice, 10) || 0), 0);
    },
    allBooksHaveOriginalPrice() {
      if (this.boothForm.saleType !== 0) return false;
      return this.books.length > 0 && this.books.every(b => b.originalPrice && parseInt(b.originalPrice, 10) > 0);
    },
    calculatedDiscountRate() {
      if (!this.allBooksHaveOriginalPrice) return 0;
      const original = this.calculatedTotalOriginalPrice;
      const current = this.effectivePrice;
      if (original > current && original > 0 && current > 0) {
        return Math.round(((original - current) / original) * 100);
      }
      return 0;
    },
    effectiveBoothName() {
      if (this.boothForm.name && this.boothForm.name.trim()) {
        return this.boothForm.name.trim();
      }
      if (this.books.length === 1) {
        return this.books[0].name || 'Sách cũ';
      }
      const firstTitle = this.books[0].name ? this.books[0].name.trim() : 'Bộ sách';
      return `Combo ${this.books.length} cuốn: ${firstTitle}`;
    },
    previewActiveImage() {
      if (this.activePreviewMedia) return this.activePreviewMedia;
      if (this.stackPreview) return this.stackPreview;
      if (this.bookPreviews[0] && this.bookPreviews[0].cover) return this.bookPreviews[0].cover;
      return '';
    },
    effectiveActivePreviewKey() {
      if (this.activePreviewKey) return this.activePreviewKey;
      if (this.stackPreview) return 'stack';
      if (this.bookPreviews[0] && this.bookPreviews[0].cover) return 'book_0_cover';
      return null;
    },
    activePreviewBookItem() {
      const key = this.effectiveActivePreviewKey || '';
      if (key === 'stack' || !key) return null;
      const parts = key.split('_');
      const idx = parseInt(parts[1], 10);
      if (isNaN(idx) || !this.books[idx]) return null;
      return this.books[idx];
    },
    selectedCategoriesList() {
      if (!this.currentBook || !this.currentBook.categories) return [];
      let ids = [];
      try {
        ids = JSON.parse(this.currentBook.categories);
      } catch (e) {
        ids = [];
      }
      return this.categoryOptions.filter(c => ids.includes(c.value));
    },
    filteredCategoryOptions() {
      if (!this.categorySearchQuery || !this.categorySearchQuery.trim()) {
        return this.categoryOptions;
      }
      const q = this.categorySearchQuery.trim().toLowerCase();
      return this.categoryOptions.filter(c => c.label.toLowerCase().includes(q));
    },
    sellerDisplayName() {
      if (this.isEditMode && this.editBoothData && this.editBoothData.createByName) {
        return this.editBoothData.createByName;
      }
      try {
        const raw = localStorage.getItem('current-account');
        if (raw) {
          const acc = JSON.parse(raw);
          if (acc.fullname) return acc.fullname;
          if (acc.name) return acc.name;
          if (acc.username) return acc.username;
        }
      } catch (e) {
        // ignore
      }
      return 'Người bán sách';
    },
    formattedCreatedDate() {
      if (this.isEditMode && this.editBoothData && this.editBoothData.createTime) {
        return this.formatDate(this.editBoothData.createTime);
      }
      const now = new Date();
      const dd = String(now.getDate()).padStart(2, '0');
      const mm = String(now.getMonth() + 1).padStart(2, '0');
      const yyyy = now.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    }
  },
  watch: {
    'currentBook.perStatus'(newVal) {
      if (!this.currentBook) return;
      const val = Number(newVal);
      if (val >= 95) this.currentBook.verStatus = 0;
      else if (val >= 85) this.currentBook.verStatus = 1;
      else if (val >= 70) this.currentBook.verStatus = 2;
      else if (val >= 50) this.currentBook.verStatus = 3;
      else this.currentBook.verStatus = 4;
    },
    showZoomModal(open) {
      if (open) {
        document.body.style.overflow = 'hidden';
        window.addEventListener('keydown', this.handleZoomKeydown);
      } else {
        document.body.style.overflow = '';
        window.removeEventListener('keydown', this.handleZoomKeydown);
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
    this.fetchCategoryOptions();
    const id = this.$route.params.id;
    if (id) {
      this.isEditMode = true;
      this.editBoothId = id;
      this.loadBoothForEdit(id);
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('keydown', this.handleZoomKeydown);
  },
  methods: {
    createEmptyBook() {
      return {
        id: null,
        idBook: '',
        name: '',
        author: '',
        categories: JSON.stringify([]),
        perStatus: 95,
        verStatus: 0,
        description: '',
        originalPrice: null,
        price: null,
        totalPage: 0
      };
    },
    selectSaleType(type) {
      this.boothForm.saleType = type;
      if (type === 1 && this.boothForm.totalBooks < 2) {
        this.setBookCount(2);
        this.$toast.info('Hình thức bán combo yêu cầu tối thiểu từ 2 cuốn sách trở lên. Đã tự động chọn 2 cuốn.');
      }
    },
    setBookCount(n) {
      if (this.boothForm.saleType === 1 && n < 2) {
        this.$toast.warning('Hình thức bán combo yêu cầu tối thiểu 2 cuốn sách!');
        n = 2;
      }
      if (n < 1) n = 1;
      if (n > 8) n = 8;
      this.boothForm.totalBooks = n;

      while (this.books.length < n) {
        this.books.push(this.createEmptyBook());
        this.bookFiles.push({ cover: null, coverBack: null });
        this.bookPreviews.push({ cover: null, coverBack: null });
      }

      if (this.books.length > n) {
        this.books = this.books.slice(0, n);
        this.bookFiles = this.bookFiles.slice(0, n);
        this.bookPreviews = this.bookPreviews.slice(0, n);
      }

      if (this.currentBookIndex >= n) {
        this.currentBookIndex = n - 1;
      }
    },
    isBookComplete(idx) {
      const b = this.books[idx];
      const p = this.bookPreviews[idx];
      if (!b || !b.name || !b.name.trim() || !b.description || !b.description.trim()) return false;
      if (!p || !p.cover || !p.coverBack) return false;
      if (this.boothForm.saleType === 0 && (!b.price || parseInt(b.price, 10) < 10000)) return false;
      return true;
    },
    handleDocumentClick(e) {
      if (this.showCatalogDropdown) {
        const container = this.$el ? this.$el.querySelector('.custom-catalog-lookup-wrapper') : null;
        if (container && !container.contains(e.target)) {
          this.showCatalogDropdown = false;
        }
      }
      if (this.showCategoryDropdown) {
        const catWrapper = this.$el ? this.$el.querySelector('.custom-category-select-wrapper') : null;
        if (catWrapper && !catWrapper.contains(e.target)) {
          this.showCategoryDropdown = false;
        }
      }
    },
    fetchCategoryOptions() {
      this.isCategoriesLoading = true;
      axios.get('category/v1/get-all', { _skip403Redirect: true })
        .then(res => {
          if (res.data.status === '200' && res.data.result) {
            const list = Array.isArray(res.data.result) ? res.data.result : (res.data.result.content || []);
            this.categoryOptions = list.map(c => ({
              value: c.id || '',
              label: c.name || c.title || c.categoryName || ''
            })).filter(c => c.value && c.label);
          }
        })
        .catch(err => console.error(err))
        .finally(() => { this.isCategoriesLoading = false; });
    },
    toggleCategoryDropdown() {
      this.showCategoryDropdown = !this.showCategoryDropdown;
      if (this.showCategoryDropdown) {
        this.categorySearchQuery = '';
      }
    },
    isCategorySelected(val) {
      if (!this.currentBook || !this.currentBook.categories) return false;
      try {
        const ids = JSON.parse(this.currentBook.categories);
        return ids.includes(val);
      } catch (e) {
        return false;
      }
    },
    toggleCategory(val) {
      if (!this.currentBook) return;
      let ids = [];
      try {
        ids = JSON.parse(this.currentBook.categories) || [];
      } catch (e) {
        ids = [];
      }
      const idx = ids.indexOf(val);
      if (idx > -1) {
        ids.splice(idx, 1);
      } else {
        ids.push(val);
      }
      this.currentBook.categories = JSON.stringify(ids);
    },
    removeCategoryTag(val) {
      this.toggleCategory(val);
    },
    clearAllCategories() {
      if (this.currentBook) {
        this.currentBook.categories = JSON.stringify([]);
      }
    },
    onCatalogInput() {
      if (this.catalogDebounceTimer) clearTimeout(this.catalogDebounceTimer);
      if (!this.catalogSearchKey || !this.catalogSearchKey.trim()) {
        this.catalogSuggestions = [];
        this.showCatalogDropdown = false;
        return;
      }
      this.isSearchingCatalog = true;
      this.showCatalogDropdown = true;

      this.catalogDebounceTimer = setTimeout(() => {
        axios.get('book/v1/search', {
          params: { key: this.catalogSearchKey.trim(), page: 0, size: 4 }
        })
          .then(res => {
            if (res.data.status === '200' && res.data.result) {
              const content = res.data.result.content || [];
              this.catalogSuggestions = content.map(b => ({
                ...b,
                cover: b.cover && !(b.cover.startsWith('data:') || b.cover.startsWith('http'))
                  ? 'data:image/png;base64,' + b.cover
                  : b.cover
              }));
            } else {
              this.catalogSuggestions = [];
            }
          })
          .catch(() => { this.catalogSuggestions = []; })
          .finally(() => { this.isSearchingCatalog = false; });
      }, 250);
    },
    selectCatalogBook(book) {
      if (!this.currentBook) return;
      this.currentBook.name = book.name || '';
      this.currentBook.idBook = book.id || '';
      this.currentBook.author = book.authors && book.authors.length > 0
        ? book.authors.map(a => a.name).join(', ')
        : '';
      
      let catIds = [];
      if (Array.isArray(book.categories)) {
        catIds = book.categories.map(c => typeof c === 'object' ? (c.id || c.value) : c).filter(Boolean);
      } else if (book.idCategory) {
        catIds = [book.idCategory];
      }
      this.currentBook.categories = JSON.stringify(catIds);
      this.catalogSearchKey = book.name || '';
      this.showCatalogDropdown = false;
    },
    clearCatalogSelection() {
      this.catalogSearchKey = '';
      if (this.currentBook) {
        this.currentBook.name = '';
        this.currentBook.idBook = '';
        this.currentBook.author = '';
        this.currentBook.categories = JSON.stringify([]);
      }
    },
    getAuthorsString(book) {
      if (!book || !book.authors || book.authors.length === 0) return 'Khuyết danh';
      return book.authors.map(a => a.name).join(', ');
    },
    selectCondition(verVal) {
      if (!this.currentBook) return;
      this.currentBook.verStatus = verVal;
      const currentPer = Number(this.currentBook.perStatus);
      if (verVal === 0 && currentPer < 95) this.currentBook.perStatus = 95;
      else if (verVal === 1 && (currentPer < 85 || currentPer >= 95)) this.currentBook.perStatus = 90;
      else if (verVal === 2 && (currentPer < 70 || currentPer >= 85)) this.currentBook.perStatus = 80;
      else if (verVal === 3 && (currentPer < 50 || currentPer >= 70)) this.currentBook.perStatus = 60;
      else if (verVal === 4 && currentPer >= 50) this.currentBook.perStatus = 40;
    },
    insertTemplateDescription() {
      if (!this.currentBook) return;
      const templates = [
        "Sách giữ gìn rất mới, chữ in sắc nét không bị nhòe. Bìa giữ kỹ không rách hay gập góc, không gạch chân bên trong.",
        "Sách cá nhân đọc xong nhượng lại, chất lượng giấy tốt, gáy sách nguyên vẹn không sút chỉ. Có gạch nhẹ vài câu hay bằng bút chì.",
        "Sách bảo quản cẩn thận trong tủ kính. Trang sách phẳng đẹp, thích hợp đọc nghiền ngẫm hoặc sưu tầm."
      ];
      this.currentBook.description = templates[Math.floor(Math.random() * templates.length)];
    },
    onOriginalPriceInput(e) {
      const digits = String(e.target.value).replace(/\D/g, '');
      if (!this.currentBook) return;
      this.currentBook.originalPrice = digits ? parseInt(digits, 10) : null;
    },
    onBookPriceInput(e) {
      const digits = String(e.target.value).replace(/\D/g, '');
      if (!this.currentBook) return;
      this.currentBook.price = digits ? parseInt(digits, 10) : null;
    },
    formatPriceInput(val) {
      if (!val && val !== 0) return '';
      const num = parseInt(val, 10);
      if (isNaN(num)) return '';
      return num.toLocaleString('vi-VN');
    },
    formatPrice(val) {
      if (!val && val !== 0) return '0 VNĐ';
      const num = parseInt(val, 10);
      if (isNaN(num)) return '0 VNĐ';
      return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(num);
    },
    formatWeight(grams) {
      if (!grams || grams <= 0) return '0g';
      if (grams >= 1000) return (grams / 1000).toFixed(1).replace('.0', '') + ' kg';
      return grams + 'g';
    },
    getConditionLabel(verStatus) {
      const opt = this.conditionOptions.find(o => o.value === verStatus);
      return opt ? opt.label : 'Rất mới';
    },
    triggerStackUpload() {
      if (this.$refs.stackInput) this.$refs.stackInput.click();
    },
    handleStackFileChange(e) {
      const file = e.target.files[0];
      this.processStackFile(file);
    },
    handleStackDrop(e) {
      const file = e.dataTransfer.files[0];
      this.processStackFile(file);
    },
    processStackFile(file) {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.$toast.error('Vui lòng chọn file hình ảnh (PNG, JPG, WEBP)!');
        return;
      }
      if (file.size > 4 * 1024 * 1024) {
        this.$toast.error('Ảnh chồng sách tối đa 4MB!');
        return;
      }
      this.stackFile = file;
      this.stackPreview = URL.createObjectURL(file);
      this.$toast.success('Đã tải ảnh chồng sách thành công!');
    },
    removeStackImage() {
      this.stackFile = null;
      this.stackPreview = null;
      if (this.$refs.stackInput) this.$refs.stackInput.value = '';
    },
    triggerBookUpload(field) {
      if (field === 'cover' && this.$refs.bookCoverInput) this.$refs.bookCoverInput.click();
      if (field === 'coverBack' && this.$refs.bookCoverBackInput) this.$refs.bookCoverBackInput.click();
    },
    handleBookFileChange(e, field) {
      const file = e.target.files[0];
      this.processBookFile(file, field);
    },
    handleBookDrop(e, field) {
      const file = e.dataTransfer.files[0];
      this.processBookFile(file, field);
    },
    processBookFile(file, field) {
      if (!file) return;
      if (!file.type.startsWith('image/')) {
        this.$toast.error('Vui lòng chọn file hình ảnh!');
        return;
      }
      if (file.size > 3 * 1024 * 1024) {
        this.$toast.error('Kích thước ảnh tối đa 3MB!');
        return;
      }
      const idx = this.currentBookIndex;
      if (!this.bookFiles[idx]) this.bookFiles[idx] = { cover: null, coverBack: null };
      if (!this.bookPreviews[idx]) this.bookPreviews[idx] = { cover: null, coverBack: null };

      this.bookFiles[idx][field] = file;
      this.bookPreviews[idx][field] = URL.createObjectURL(file);
    },
    removeBookImage(field) {
      const idx = this.currentBookIndex;
      if (this.bookFiles[idx]) this.bookFiles[idx][field] = null;
      if (this.bookPreviews[idx]) this.bookPreviews[idx][field] = null;
      if (field === 'cover' && this.$refs.bookCoverInput) this.$refs.bookCoverInput.value = '';
      if (field === 'coverBack' && this.$refs.bookCoverBackInput) this.$refs.bookCoverBackInput.value = '';
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}/${mm}/${yyyy}`;
    },
    handleImageError(event) {
      if (event && event.target) {
        event.target.onerror = null;
        event.target.src = "data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='160' viewBox='0 0 120 160'%3E%3Crect width='100%25' height='100%25' fill='%23f5f2ea'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%23b5b0a5' font-family='sans-serif' font-size='14'%3ES%C3%A1ch%3C/text%3E%3C/svg%3E";
      }
    },
    loadBoothForEdit(id) {
      this.isLoadingEdit = true;
      axios.get(`/old-book/v1/detail/${id}`)
        .then(res => {
          if (res.data.status === '200' && res.data.result) {
            const data = res.data.result;
            this.editBoothData = data;
            if (data.status === 2 || data.status === 4) {
              this.$toast.error('Gian hàng ở trạng thái Đã bán hoặc Đã hủy không được chỉnh sửa!');
              this.$router.push('/my-old-books');
              return;
            }

            this.boothForm.name = data.name || '';
            this.boothForm.saleType = data.saleType !== undefined ? data.saleType : 0;
            this.boothForm.weight = data.weight || 0;
            this.boothForm.price = data.price || '';
            this.boothForm.description = data.description || '';

            if (data.stackImage) {
              this.stackPreview = data.stackImage.startsWith('data:') ? data.stackImage : 'data:image/png;base64,' + data.stackImage;
            }

            const rawBooks = data.books || [];
            this.setBookCount(rawBooks.length || 1);

            for (let i = 0; i < rawBooks.length; i++) {
              const rb = rawBooks[i];
              this.books[i] = {
                id: rb.id,
                idBook: rb.idBook || '',
                name: rb.name || '',
                author: rb.author || '',
                categories: rb.categories || JSON.stringify([]),
                perStatus: rb.perStatus !== undefined ? rb.perStatus : 95,
                verStatus: rb.verStatus !== undefined ? rb.verStatus : 0,
                description: rb.description || '',
                originalPrice: rb.originalPrice || null,
                price: rb.price || null,
                totalPage: rb.totalPage || 0
              };

              this.bookPreviews[i] = {
                cover: rb.cover ? (rb.cover.startsWith('data:') ? rb.cover : 'data:image/png;base64,' + rb.cover) : null,
                coverBack: rb.coverBack ? (rb.coverBack.startsWith('data:') ? rb.coverBack : 'data:image/png;base64,' + rb.coverBack) : null
              };
            }
          } else {
            this.$toast.error('Không tìm thấy thông tin gian hàng!');
            this.$router.push('/my-old-books');
          }
        })
        .catch(err => {
          console.error(err);
          this.$toast.error('Lỗi khi tải thông tin gian hàng');
          this.$router.push('/my-old-books');
        })
        .finally(() => { this.isLoadingEdit = false; });
    },
    goToPreview() {
      // Validate Booth level
      if (this.boothForm.saleType === 1) { // Combo
        if (this.books.length < 2) {
          this.$toast.error('Hình thức bán combo yêu cầu tối thiểu từ 2 cuốn sách trở lên!');
          return;
        }
        if (!this.boothForm.price || parseInt(this.boothForm.price, 10) < 10000) {
          this.$toast.error('Vui lòng nhập giá bán combo tối thiểu 10.000 VNĐ!');
          return;
        }
      }

      // Validate each book
      for (let i = 0; i < this.books.length; i++) {
        const b = this.books[i];
        const p = this.bookPreviews[i];

        if (!b.name || !b.name.trim()) {
          this.$toast.error(`Vui lòng nhập tên cuốn sách thứ ${i + 1}!`);
          this.currentBookIndex = i;
          return;
        }

        if (this.boothForm.saleType === 0) { // Single sale
          if (!b.price || parseInt(b.price, 10) < 10000) {
            this.$toast.error(`Vui lòng nhập giá bán riêng tối thiểu 10.000 VNĐ cho cuốn thứ ${i + 1}: ${b.name}!`);
            this.currentBookIndex = i;
            return;
          }
        }

        if (!b.description || !b.description.trim()) {
          this.$toast.error(`Vui lòng nhập mô tả tình trạng cho cuốn thứ ${i + 1}: ${b.name}!`);
          this.currentBookIndex = i;
          return;
        }

        if (!p || !p.cover || !p.coverBack) {
          this.$toast.error(`Vui lòng tải lên đầy đủ ảnh bìa trước và bìa sau cho cuốn thứ ${i + 1}: ${b.name}!`);
          this.currentBookIndex = i;
          return;
        }
      }

      if (this.stackPreview) {
        this.setPreviewMedia(this.stackPreview, 'Ảnh chụp chồng sách', 'stack');
      } else if (this.bookPreviews[0] && this.bookPreviews[0].cover) {
        this.setPreviewMedia(this.bookPreviews[0].cover, 'Cuốn 1 - Bìa trước', 'book_0_cover');
      }

      this.activeStep = 2;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    submitForm() {
      this.isSubmitting = true;

      const formData = new FormData();

      // Stack image if uploaded
      if (this.stackFile) {
        formData.append('stack_image', this.stackFile);
      }

      // Append book files
      for (let i = 0; i < this.books.length; i++) {
        const bf = this.bookFiles[i];
        if (bf && bf.cover) {
          formData.append(`cover_${i}`, bf.cover);
        }
        if (bf && bf.coverBack) {
          formData.append(`cover_back_${i}`, bf.coverBack);
        }
      }

      // Construct Request Data
      const requestData = {
        name: this.effectiveBoothName,
        saleType: this.boothForm.saleType,
        totalBooks: this.books.length,
        weight: this.boothForm.weight || 0,
        price: this.effectivePrice,
        description: this.boothForm.description ? this.boothForm.description.trim() : '',
        books: this.books.map((b, idx) => ({
          id: b.id,
          idBook: b.idBook || null,
          name: b.name.trim(),
          author: b.author ? b.author.trim() : null,
          categories: b.categories,
          perStatus: parseInt(b.perStatus, 10),
          verStatus: parseInt(b.verStatus, 10),
          description: b.description.trim(),
          originalPrice: b.originalPrice ? parseInt(b.originalPrice, 10) : null,
          price: this.boothForm.saleType === 0 ? (b.price ? parseInt(b.price, 10) : null) : null,
          totalPage: parseInt(b.totalPage, 10) || 0,
          cover: this.bookPreviews[idx] ? this.bookPreviews[idx].cover : null,
          coverBack: this.bookPreviews[idx] ? this.bookPreviews[idx].coverBack : null
        }))
      };

      formData.append('data', JSON.stringify(requestData));

      const apiCall = this.isEditMode
        ? axios.put(`/old-book/update/${this.editBoothId}`, formData, { headers: { "Content-Type": "multipart/form-data" } })
        : axios.post("/old-book/create", formData, { headers: { "Content-Type": "multipart/form-data" } });

      apiCall
        .then(res => {
          if (res.data.status === '200') {
            this.$toast.success(this.isEditMode ? 'Cập nhật gian hàng thành công!' : 'Đăng bán gian hàng sách cũ thành công!');
            const resultObj = res.data.result;
            const resId = (resultObj && (resultObj.id || (typeof resultObj === 'string' ? resultObj : null))) || this.editBoothId;
            this.createdBoothId = resId;
            this.activeStep = 3;
            window.scrollTo({ top: 0, behavior: 'smooth' });
          } else {
            this.$toast.error(res.data.message || 'Lưu bài đăng thất bại!');
          }
        })
        .catch(err => {
          console.error(err);
          this.$toast.error('Lỗi khi gửi dữ liệu lên máy chủ');
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    setPreviewMedia(img, label, key) {
      this.activePreviewMedia = img;
      this.activePreviewLabel = label;
      this.activePreviewKey = key || null;
    },
    scrollThumbs(direction) {
      const container = this.$refs.thumbStrip;
      if (!container) return;
      const scrollAmount = direction === 'left' ? -220 : 220;
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    },
    handleThumbWheel(e) {
      const container = this.$refs.thumbStrip;
      if (!container) return;
      if (e.deltaY !== 0) {
        e.preventDefault();
        container.scrollLeft += e.deltaY;
      }
    },
    getBookCategoriesList(book) {
      if (!book || !book.categories) return [];
      let ids = [];
      try {
        ids = JSON.parse(book.categories);
      } catch (e) {
        ids = [];
      }
      return this.categoryOptions.filter(c => ids.includes(c.value));
    },
    goToBoothDetail() {
      if (this.createdBoothId) {
        this.$router.push(`/old-books/${this.createdBoothId}`);
      } else {
        this.$router.push('/my-old-books');
      }
    },
    resetForm() {
      this.boothForm = {
        name: '',
        saleType: 0,
        totalBooks: 1,
        weight: 350,
        price: '',
        description: ''
      };
      this.stackFile = null;
      this.stackPreview = null;
      this.currentBookIndex = 0;
      this.books = [this.createEmptyBook()];
      this.bookFiles = [{ cover: null, coverBack: null }];
      this.bookPreviews = [{ cover: null, coverBack: null }];
      this.createdBoothId = null;
      this.activePreviewMedia = null;
      this.activePreviewLabel = null;
      this.activeStep = 1;
      this.isEditMode = false;
      this.editBoothId = null;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    // Zoom Modal Methods
    openZoomModal(img) {
      if (img && typeof img === 'string') {
        this.zoomImage = img;
      } else if (this.previewActiveImage) {
        this.zoomImage = this.previewActiveImage;
      }
      this.zoomScale = 1;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
      this.isDragging = false;
      this.toolbarHidden = false;
      this.showZoomModal = true;
    },
    closeZoomModal() {
      this.showZoomModal = false;
      this.zoomScale = 1;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
      this.isDragging = false;
      this.toolbarHidden = false;
    },
    zoomIn() {
      this.zoomScale = Math.min(4, +(this.zoomScale + 0.5).toFixed(1));
    },
    zoomOut() {
      this.zoomScale = Math.max(0.5, +(this.zoomScale - 0.5).toFixed(1));
      if (this.zoomScale <= 1) {
        this.zoomPanX = 0;
        this.zoomPanY = 0;
      }
    },
    resetZoom() {
      this.zoomScale = 1;
      this.zoomPanX = 0;
      this.zoomPanY = 0;
      this.isDragging = false;
      this.toolbarHidden = false;
    },
    toggleDblZoom() {
      if (this.zoomScale > 1) {
        this.resetZoom();
      } else {
        this.zoomScale = 2;
      }
    },
    handleWheelZoom(e) {
      if (!this.showZoomModal) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      const nextScale = Math.min(4, Math.max(0.5, +(this.zoomScale + delta).toFixed(2)));
      this.zoomScale = nextScale;
      if (nextScale <= 1) {
        this.zoomPanX = 0;
        this.zoomPanY = 0;
      }
    },
    handleZoomKeydown(e) {
      if (!this.showZoomModal) return;
      if (e.key === 'Escape') this.closeZoomModal();
      else if (e.key === '+' || e.key === '=') this.zoomIn();
      else if (e.key === '-' || e.key === '_') this.zoomOut();
      else if (e.key === '0') this.resetZoom();
    },
    startDrag(e) {
      if (this.zoomScale <= 1) return;
      this.isDragging = true;
      this.dragStartX = e.clientX - this.zoomPanX;
      this.dragStartY = e.clientY - this.zoomPanY;
    },
    onDrag(e) {
      if (!this.isDragging) return;
      this.zoomPanX = e.clientX - this.dragStartX;
      this.zoomPanY = e.clientY - this.dragStartY;
    },
    stopDrag() {
      this.isDragging = false;
    }
  }
};
